import React from 'react';
import { ImageBackground, StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  BodyText,
  getContentfulImageUrl,
  replaceLineBreaksWithSpaces,
  theme,
  TitleText,
  useBreakpointHelpers,
} from '@sg/garnish';

import type { SweetpassUpgradeScreenPalette } from './SweetgreenUpgradeScreenPalette';
import type { SweetpassUpgradeBenefit } from './SweetpassUpgradeScreen.loaded';

export type SweetpassBenefitCardProps = {
  readonly content: SweetpassUpgradeBenefit;
  readonly palette: SweetpassUpgradeScreenPalette['benefitList']['benefit'];
};
export const SweetpassBenefitCard = (props: SweetpassBenefitCardProps) => {
  const { isMobile } = useBreakpointHelpers();

  return isMobile ? (
    <SweetpassBenefitCardMobile {...props} />
  ) : (
    <SweetpassBenefitCardDesktop {...props} />
  );
};

const SweetpassBenefitCardDesktop = ({
  content,
  palette,
}: SweetpassBenefitCardProps) => {
  const { isTablet } = useBreakpointHelpers();

  const titleStyle = useStyle(() => ({ color: palette.titleColor }), [palette]);
  const bodyStyle = useStyle(() => ({ color: palette.bodyColor }), [palette]);
  const cardStyle = useStyle(() => ({ opacity: palette.opacity }), [palette]);

  return (
    <View style={[styles.benefitsCardDesktop, cardStyle]}>
      <ImageBackground
        style={
          isTablet ? styles.benefitsImageTablet : styles.benefitsImageDesktop
        }
        accessibilityLabel={content.image.description}
        source={{
          uri: getContentfulImageUrl(content.image.url, {
            fm: 'webp',
            q: 90,
            w: 200,
            h: 200,
            fit: 'fill',
          }),
        }}
      />
      <TitleText style={[styles.benefitsText, titleStyle]} size={5} bold>
        {content.titleText.toUpperCase()}
      </TitleText>
      <BodyText style={[styles.benefitsText, bodyStyle]} size={2}>
        {content.bodyText}
      </BodyText>
    </View>
  );
};

const SweetpassBenefitCardMobile = ({
  content,
  palette,
}: SweetpassBenefitCardProps) => {
  const titleStyle = useStyle(() => ({ color: palette.titleColor }), [palette]);
  const bodyStyle = useStyle(() => ({ color: palette.bodyColor }), [palette]);
  const cardStyle = useStyle(() => ({ opacity: palette.opacity }), [palette]);

  return (
    <View key={content.id} style={[styles.benefitsCardMobile, cardStyle]}>
      <ImageBackground
        style={styles.benefitsImageMobile}
        accessibilityLabel={content.image.description}
        source={{
          uri: getContentfulImageUrl(content.image.url, {
            fm: 'webp',
            q: 90,
            w: 120,
            h: 120,
            fit: 'fill',
          }),
        }}
      />
      <View style={styles.benefitsDetails}>
        <BodyText style={titleStyle} size={4} bold>
          {replaceLineBreaksWithSpaces(content.titleText)}
        </BodyText>
        <BodyText style={bodyStyle} size={4}>
          {content.bodyText}
        </BodyText>
      </View>
    </View>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  benefitsCardDesktop: {
    maxWidth: 336,
    flexDirection: 'column',
    alignItems: 'center',
    margin: theme.spacing.large,
    padding: theme.spacing.small,
    color: theme.colors.GREEN_2,
  },
  benefitsCardMobile: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  benefitsImageDesktop: {
    height: 200,
    width: 200,
  },
  benefitsImageTablet: {
    height: 120,
    width: 120,
  },
  benefitsImageMobile: {
    height: 80,
    width: 80,
  },
  benefitsDetails: {
    paddingLeft: theme.spacing.medium,
    minWidth: 0,
    flex: 1,
    flexDirection: 'column',
    color: theme.colors.GREEN_2,
  },
  benefitsText: {
    paddingTop: theme.spacing.medium,
    textAlign: 'center',
  },
});
